import { PropsWithChildren } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { AuthInfo, TenantContext, usePublicUserFromStorage } from "@nens/ranalib";

import { PublicationPublicGate } from "@/features/publications";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";

// ==============================
// SHARED PUBLICATION AUTH:
// ==============================

// If a user opens a shared publication link that includes an `auth` key
// in the query params (SEE_HERE_1), we fall back to Basic Auth.

// In this case:
// - Username is hardcoded as "__key__"
// - Password is the share key from the URL

// This flow uses a simplified tenant context (SEE_HERE_2),
// which only includes the tenant — no full identity or login flow.

// SEE_HERE_1
export const PUBLICATION_SHARE_KEY_KEY = "shareKey";

export function usePublicationShareKey() {
  const [publicationShareKey] = useRanaSearchState<string | undefined | null>(
    PUBLICATION_SHARE_KEY_KEY,
    undefined,
    ["string", "undefined", "null"]
  );

  return publicationShareKey;
}

export function PublicTenantProvider({
  publicationShareKey,
  children,
  tenant,
}: PropsWithChildren<{ publicationShareKey: string; tenant: string }>) {
  const [publicUser, setPublicUser] = usePublicUserFromStorage();
  const authorizationHeader = `Basic ${btoa(`__key__:${publicationShareKey}`)}`;
  getRanaApiV1().setAccessToken(authorizationHeader);
  getRanaApiV1().setTenantId(tenant);

  if (!publicUser) return <PublicationPublicGate setPublicUser={setPublicUser} />;

  // SEE_HERE_2
  const tenantContext: AuthInfo = {
    tenant,
    isPublic: true,
    user: publicUser,
    setPublicUser,
    authorizationHeader,
  };

  return <TenantContext.Provider value={tenantContext}>{children}</TenantContext.Provider>;
}
